"use client";
import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LuCalendarDays } from "react-icons/lu";
import { GoPencil } from "react-icons/go";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

export default function CalendarDropdown({ id, openDropdown, setOpenDropdown, value, onChange }) {
  const today = new Date();

  const parseInitial = (val) => {
    if (!val) {
      return {
        month: today.toLocaleString("default", { month: "long" }),
        day: today.getDate(),
        year: today.getFullYear(),
      };
    }

    const parts = val.replace(",", "").split(" ").filter(Boolean);
    let month = parts[0] || today.toLocaleString("default", { month: "long" });
    let day = parts[1] ? parseInt(parts[1], 10) : today.getDate();
    let year = parts[2] ? parseInt(parts[2], 10) : today.getFullYear();

    if (!MONTHS.includes(month)) {
      month = today.toLocaleString("default", { month: "long" });
    }
    if (!day || isNaN(day)) day = today.getDate();
    if (!year || isNaN(year)) year = today.getFullYear();

    return { month, day, year };
  };

  const initial = parseInitial(value);

  const [selectedMonth, setSelectedMonth] = useState(initial.month);
  const [selectedDay, setSelectedDay] = useState(initial.day);
  const [selectedYear, setSelectedYear] = useState(initial.year);

  const isOpen = openDropdown === id;

  const toggleOpen = () => setOpenDropdown(isOpen ? null : id);

  const handleSave = () => {
    setOpenDropdown(null);
    const result = `${selectedMonth} ${selectedDay}, ${selectedYear}`;
    onChange(result);
  };

  const monthIndex = MONTHS.indexOf(selectedMonth);
  const daysInMonth =
    monthIndex === -1
      ? 31
      : new Date(selectedYear, monthIndex + 1, 0).getDate();
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  useEffect(() => {
    if (selectedDay > daysInMonth) {
      setSelectedDay(daysInMonth);
    }
  }, [daysInMonth, selectedDay]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".calendar-dropdown")) setOpenDropdown(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setOpenDropdown]);

  // диапазон годов, например тек.год - 5 .. +5
  const currentYear = today.getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  return (
    <div className="relative calendar-dropdown">
      {/* Input */}
      <div
        className="flex items-center justify-between border border-gray-300 rounded-[14px] px-3 py-3 cursor-pointer bg-white hover:bg-gray-50 transition"
        onClick={toggleOpen}
      >
        <div className="flex items-center gap-2">
          <LuCalendarDays className="text-gray-500 w-5 h-5" />
          <span className="text-sm text-gray-800">
            {`${selectedMonth} ${selectedDay}, ${selectedYear}`}
          </span>
        </div>
        <GoPencil className="text-gray-400 w-4 h-4" />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="absolute top-[105%] left-0 w-auto bg-white border border-gray-200 rounded-2xl shadow-lg z-50 p-4"
          >
            <div className="flex gap-2 mb-3">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border border-gray-200 rounded-lg py-2 px-2 text-sm outline-none"
              >
                {MONTHS.map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>

              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                className="border border-gray-200 rounded-lg py-2 px-2 text-sm outline-none"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-3">
              {daysArray.map((d) => (
                <div
                  key={d}
                  onClick={() => setSelectedDay(d)}
                  className={`text-sm flex items-center justify-center w-8 h-8 rounded-lg cursor-pointer ${
                    d === selectedDay
                      ? "bg-[#020637] text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {d}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1.5 text-xs rounded-md border border-gray-200 hover:bg-gray-100"
                onClick={() => setOpenDropdown(null)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 text-xs rounded-md bg-[#020637] text-white hover:bg-[#0e1c7a]"
                onClick={handleSave}
              >
                Save
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
